/**
 * Language Selector Component
 * Dropdown for selecting the interface language with support for 10 languages
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext, AVAILABLE_LANGUAGES, SupportedLanguage } from '../../contexts/LanguageContext';
import { useThemeContext } from '../../contexts/ThemeContext';

/**
 * Language Selector props
 */
export interface LanguageSelectorProps {
  minimal?: boolean;
  showFlag?: boolean;
  showNativeName?: boolean;
  className?: string;
}

/**
 * Language Selector Component
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  minimal = false,
  showFlag = true,
  showNativeName = true,
  className = '',
}) => {
  const { t } = useTranslation();
  const { currentLanguage, setLanguage, isRTL, getCurrentLanguageInfo } = useLanguageContext();
  const { theme } = useThemeContext();
  
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = getCurrentLanguageInfo();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleLanguageSelect = async (langCode: SupportedLanguage) => {
    await setLanguage(langCode);
    setIsOpen(false);
  };

  // Minimal mode - just flag/code
  if (minimal) {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 px-2 py-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2"
          aria-label={t('language.select')}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          {showFlag && <span className="text-lg">{currentLang.flag}</span>}
          <span className="text-sm font-medium uppercase">{currentLanguage}</span>
          <ChevronIcon isOpen={isOpen} />
        </button>

        {isOpen && (
          <LanguageDropdown
            languages={AVAILABLE_LANGUAGES}
            currentLanguage={currentLanguage}
            onSelect={handleLanguageSelect}
            isRTL={isRTL}
            showFlag={showFlag}
            showNativeName={showNativeName}
            theme={theme}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-md border hover:bg-gray-50 focus:outline-none focus:ring-2 transition-colors ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}
        style={{
          borderColor: theme?.colors.accent,
        }}
        aria-label={t('language.select')}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {showFlag && <span className="text-xl">{currentLang.flag}</span>}
        <div className={`text-left ${isRTL ? 'text-right' : ''}`}>
          <span 
            className="text-sm font-medium block"
            style={{ color: theme?.colors.text }}
          >
            {showNativeName ? currentLang.nativeName : currentLang.name}
          </span>
        </div>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <LanguageDropdown
          languages={AVAILABLE_LANGUAGES}
          currentLanguage={currentLanguage}
          onSelect={handleLanguageSelect}
          isRTL={isRTL}
          showFlag={showFlag}
          showNativeName={showNativeName}
          theme={theme}
        />
      )}
    </div>
  );
};

/**
 * Language Dropdown Component
 */
interface LanguageDropdownProps {
  languages: typeof AVAILABLE_LANGUAGES;
  currentLanguage: SupportedLanguage;
  onSelect: (code: SupportedLanguage) => void;
  isRTL: boolean;
  showFlag: boolean;
  showNativeName: boolean;
  theme: any;
}

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
  languages,
  currentLanguage,
  onSelect,
  isRTL,
  showFlag,
  showNativeName,
  theme,
}) => {
  return (
    <div 
      className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-56 bg-white rounded-lg shadow-lg border z-50 py-1 max-h-80 overflow-y-auto`}
      role="listbox"
      aria-label="Select language"
    >
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => onSelect(lang.code)}
          className={`w-full flex items-center px-4 py-3 hover:bg-gray-100 transition-colors ${isRTL ? 'flex-row-reverse text-right' : 'text-left'} ${currentLanguage === lang.code ? 'bg-primary/5' : ''}`}
          role="option"
          aria-selected={currentLanguage === lang.code}
        >
          {showFlag && (
            <span className={`text-xl ${isRTL ? 'ml-3' : 'mr-3'}`}>
              {lang.flag}
            </span>
          )}
          <div className="flex-1">
            <span 
              className="block text-sm font-medium"
              style={{ color: theme?.colors.text }}
            >
              {showNativeName ? lang.nativeName : lang.name}
            </span>
            {showNativeName && (
              <span 
                className="block text-xs"
                style={{ color: theme?.colors.textSecondary }}
              >
                {lang.name}
              </span>
            )}
          </div>
          {currentLanguage === lang.code && (
            <CheckIcon className={isRTL ? 'mr-2' : 'ml-2'} color={theme?.colors.primary} />
          )}
        </button>
      ))}
    </div>
  );
};

/**
 * Inline Language Selector - for use in kiosk mode or landing pages
 */
export interface InlineLanguageSelectorProps {
  columns?: 2 | 3 | 5;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const InlineLanguageSelector: React.FC<InlineLanguageSelectorProps> = ({
  columns = 5,
  size = 'md',
  className = '',
}) => {
  const { currentLanguage, setLanguage, isRTL } = useLanguageContext();
  const { theme } = useThemeContext();

  const sizeClasses = {
    sm: 'p-2 text-sm',
    md: 'p-3 text-base',
    lg: 'p-4 text-lg min-h-[64px]', // Kiosk-friendly size
  };

  const columnClasses = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    5: 'grid-cols-2 sm:grid-cols-5',
  };

  return (
    <div 
      className={`grid ${columnClasses[columns]} gap-3 ${className}`}
      role="radiogroup"
      aria-label="Select language"
    >
      {AVAILABLE_LANGUAGES.map((lang) => (
        <button
          key={lang.code}
          onClick={() => setLanguage(lang.code)}
          className={`flex flex-col items-center justify-center rounded-lg border-2 transition-all ${sizeClasses[size]} ${
            currentLanguage === lang.code
              ? 'ring-2 ring-offset-2'
              : 'hover:bg-gray-50'
          } ${isRTL ? 'text-right' : 'text-left'}`}
          style={{
            borderColor: currentLanguage === lang.code 
              ? theme?.colors.primary 
              : theme?.colors.accent,
            ringColor: theme?.colors.primary,
          }}
          role="radio"
          aria-checked={currentLanguage === lang.code}
        >
          <span className="text-2xl mb-1">{lang.flag}</span>
          <span 
            className="font-medium"
            style={{ color: theme?.colors.text }}
          >
            {lang.nativeName}
          </span>
        </button>
      ))}
    </div>
  );
};

/**
 * Icon Components
 */
const ChevronIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg 
    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string; color?: string }> = ({ className, color }) => (
  <svg 
    className={`w-5 h-5 ${className}`} 
    fill="none" 
    stroke={color || 'currentColor'} 
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

export default LanguageSelector;